/**
 * Internal Access Utilities
 * Client-side gate for internal-only routes/components (e.g., Analytics Dashboard).
 *
 * Notes
 * - This is NOT a replacement for server-side auth, but sufficient to hide
 *   internal tooling in a static site / demo context.
 * - Access is granted via a short passcode stored in Vite env `VITE_INTERNAL_ACCESS_CODE`.
 * - Once unlocked, a flag is persisted in localStorage so teammates don't
 *   need to enter the code every visit.
 */

export const INTERNAL_STORAGE_KEY = 'ns_internal_access';

/** Returns the configured internal access code from env (empty if not set). */
export const getInternalAccessCode = (): string => {
  try {
    // Vite exposes env vars prefixed with VITE_
    return import.meta.env.VITE_INTERNAL_ACCESS_CODE?.toString() ?? '';
  } catch {
    return '';
  }
};

/** Returns true if internal access has been granted in this browser. */
export const hasInternalAccess = (): boolean => {
  try {
    return sessionStorage.getItem(INTERNAL_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

/** Persist an unlocked flag. */
export const grantInternalAccess = (): void => {
  try {
    sessionStorage.setItem(INTERNAL_STORAGE_KEY, '1');
  } catch {
    /* ignore */
  }
};

/** Clear unlocked flag. */
export const revokeInternalAccess = (): void => {
  try {
    sessionStorage.removeItem(INTERNAL_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Attempt to unlock with a user-provided passcode.
 * Returns true on success.
 */
export const tryUnlockInternal = (codeInput: string): boolean => {
  const expected = getInternalAccessCode();
  if (!expected) return false;
  if ((codeInput || '').trim() === expected.trim()) {
    grantInternalAccess();
    return true;
  }
  return false;
};

/**
 * Optional convenience: allow unlocking via `?internal=<code>` query param.
 */
export const unlockFromQueryParam = (): void => {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('internal');
    if (code && tryUnlockInternal(code)) {
      // remove the param from the URL without reloading
      const url = new URL(window.location.href);
      url.searchParams.delete('internal');
      window.history.replaceState({}, document.title, url.toString());
    }
  } catch {
    /* ignore */
  }
};


